import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useSelector } from 'react-redux';
import { BREEZE_INTELLIGENCE_CREDITS_100, CRM_SUITE_STARTER_BUNDLE } from 'self-service-api/constants/ApiNames';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { BREEZE_INTELLIGENCE_STARTER_PLATFORM_CARD } from '../../constants/ErrorBoundaryNames';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { usePurchaseOptionsForProductsForTerm } from '../../hooks/usePurchaseOptions';
import { useAppSelector } from '../../reducers';
import { commonAllowedQueryParamTermSelector } from '../../selectors/OfferSelectors';
import { suiteProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { currentSkuIdForProductSelector, productForSkuIdSelector, skuIdHasBundledProductsSelector } from '../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMultipleSkuIdsSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm, useValidatedCrmSuiteQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import DetailedTermSelector from '../common/TermSelectors/DetailedTermSelector';
import Whitespace from '../common/Whitespace';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import withErrorBoundary from '../error-boundary/withErrorBoundary';
import { PriceDisplayStyled } from '../merch-card/price-display/PriceDisplayStyled';
import BreezeCardCTAsContainer from './BreezeCardCTAsContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BreezeIntelligenceStarterPlatformCard = ({
  screen,
  trackingSource
}) => {
  const isAppReady = useGlobalLoading();
  const {
    queryParamTerm: unbundledQueryParamTerm
  } = useValidatedCrmSuiteQueryParamTerm({
    productLevel: STARTER
  });
  const {
    queryParamTerm,
    setQueryParamTerm
  } = useQueryParamTerm();
  const suiteProductConfigurations = useSelector(state => suiteProductConfigurationsSelector(state, {
    productLevel: STARTER
  }));
  const hasBundledSuite = useAppSelector(state => suiteProductConfigurations.find(config => skuIdHasBundledProductsSelector(state, {
    skuId: config.skuId
  })));
  const breezeIntelligenceCreditsSmallSkuId = useSelector(state => {
    return currentSkuIdForProductSelector(state, {
      apiName: BREEZE_INTELLIGENCE_CREDITS_100
    });
  });
  const breezeIntelligenceCreditsSmallProductConfig = breezeIntelligenceCreditsSmallSkuId ? createProductConfig({
    quantity: 1,
    skuId: breezeIntelligenceCreditsSmallSkuId
  }) : null;
  const combinedProductConfigurations = [...suiteProductConfigurations, ...(breezeIntelligenceCreditsSmallProductConfig ? [breezeIntelligenceCreditsSmallProductConfig] : [])];
  const skuIds = combinedProductConfigurations.map(config => config.skuId);
  const queryParamTermToUse = hasBundledSuite ? queryParamTerm : unbundledQueryParamTerm || queryParamTerm;
  const {
    loading: loadingPricingData,
    pricingData: pricingInfo
  } = usePurchaseOptionsForProductsForTerm({
    productConfigurations: combinedProductConfigurations,
    term: queryParamTermToUse
  });
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const shouldShowTermSelector = useAppSelector(state => {
    if (!isAppReady) return false;
    return shouldShowTermSelectorForMultipleSkuIdsSelector(state, {
      skuIds
    });
  });
  const commonAllowedQueryParamTerm = useSelector(state => commonAllowedQueryParamTermSelector(state, {
    skuIds,
    selectedTerm: queryParamTermToUse
  }));
  const purchaseMotionsOverride = commonAllowedQueryParamTerm ? [BUY_TOUCHLESS] : undefined;
  const skuIdToUseForPrice = skuIds[0];
  const apiName = useAppSelector(state => {
    if (skuIdToUseForPrice) {
      const product = productForSkuIdSelector(state, {
        skuId: skuIdToUseForPrice
      });
      return product === null || product === void 0 ? void 0 : product.apiName;
    }
  });
  if (!breezeIntelligenceCreditsSmallProductConfig) return null;
  const productIdentifier = hasBundledSuite ? CRM_SUITE_STARTER_BUNDLE : `crm-suite-${STARTER}`;
  const loading = loadingPricingData || !isAppReady;
  const termSelectorToShow = /*#__PURE__*/_jsx(DetailedTermSelector, {
    productConfigurations: combinedProductConfigurations,
    onChange: setQueryParamTerm,
    trackingSource: trackingSource,
    activeTerm: queryParamTerm
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    "data-selenium-id": `${productIdentifier}-merch-card`,
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h2",
      typography: "heading-300",
      paddingBlockStart: "500",
      paddingInline: "700",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pages.breezeIntelligence.starterCard.title"
      })
    }), /*#__PURE__*/_jsxs(CardSection, {
      paddingInline: "700",
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: ['100%'],
        children: pricingInfo && /*#__PURE__*/_jsx(PriceDisplayStyled, {
          pricingInfo: pricingInfo,
          seleniumId: apiName || 'loading',
          currencyCode: currencyCode,
          currencyCodeUnit: currencyCodeUnit
        })
      }), shouldShowTermSelector ? /*#__PURE__*/_jsx(View, {
        paddingBlock: "500",
        children: termSelectorToShow
      }) : /*#__PURE__*/_jsx(Whitespace, {
        height: 20
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        fallback: apiName ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
          apiName: apiName,
          trackingSource: trackingSource,
          screen: screen
        }) : null,
        children: /*#__PURE__*/_jsx(BreezeCardCTAsContainer, {
          productConfigurations: combinedProductConfigurations,
          trackingSource: trackingSource,
          returnFirstCTAOnly: true,
          screen: screen,
          purchaseMotionsOverride: purchaseMotionsOverride
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "detail-200",
        use: "help",
        marginBlockStart: "500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.breezeIntelligence.starterCard.disclaimer"
        })
      })]
    })]
  });
};
export default withErrorBoundary(BreezeIntelligenceStarterPlatformCard, {
  fallback: null,
  errorBoundaryName: BREEZE_INTELLIGENCE_STARTER_PLATFORM_CARD
});